import {
  AUTH_MESSAGES,
  COGNITO_ERRORS,
  HTTP_STATUS,
} from '../constants/auth-constants';
import { AuthError, AuthErrorType } from '../models/auth.model';

function isHttpError(
  error: unknown
): error is { status: number; message?: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof (error as { status: unknown }).status === 'number'
  );
}

function getErrorName(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const err = error as { name?: string; code?: string };
    return err.name ?? err.code ?? '';
  }
  return '';
}

function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const err = error as { message?: string };
    if (typeof err.message === 'string') {
      return err.message;
    }
  }
  if (typeof error === 'string') {
    return error;
  }
  return '';
}

const ERROR_RULES: Array<{
  match: (name: string, message: string, status?: number) => boolean;
  type: AuthErrorType;
  userMessage: string;
}> = [
  {
    match: (_, message) =>
      message.toLowerCase().includes('refresh token') &&
      (message.toLowerCase().includes('expired') ||
        message.toLowerCase().includes('invalid')),
    type: 'REFRESH_TOKEN_EXPIRED',
    userMessage: AUTH_MESSAGES.SESSION_EXPIRED,
  },
  {
    match: (_, message) =>
      message.toLowerCase().includes('token') &&
      message.toLowerCase().includes('expired'),
    type: 'TOKEN_EXPIRED',
    userMessage: AUTH_MESSAGES.SESSION_EXPIRED,
  },
  {
    match: (name) =>
      name === COGNITO_ERRORS.CODE_MISMATCH ||
      name === COGNITO_ERRORS.NOT_AUTHORIZED,
    type: 'INVALID_OTP',
    userMessage: AUTH_MESSAGES.INVALID_OTP,
  },
  {
    match: (name) => name === COGNITO_ERRORS.EXPIRED_CODE,
    type: 'OTP_EXPIRED',
    userMessage: AUTH_MESSAGES.OTP_EXPIRED,
  },
  {
    match: (name) => name === COGNITO_ERRORS.USER_NOT_FOUND,
    type: 'USER_NOT_FOUND',
    userMessage: 'No account found with this email address.',
  },
  {
    match: (name) =>
      name === COGNITO_ERRORS.LIMIT_EXCEEDED ||
      name === COGNITO_ERRORS.TOO_MANY_REQUESTS,
    type: 'RATE_LIMITED',
    userMessage: AUTH_MESSAGES.RATE_LIMITED,
  },
  {
    match: (_, __, status) => status === HTTP_STATUS.UNAUTHORIZED,
    type: 'UNAUTHENTICATED',
    userMessage: AUTH_MESSAGES.SESSION_EXPIRED,
  },
  {
    match: (_, message, status) =>
      status === 0 ||
      message.toLowerCase().includes('network') ||
      message.toLowerCase().includes('fetch') ||
      message.toLowerCase().includes('connection'),
    type: 'NETWORK_ERROR',
    userMessage: AUTH_MESSAGES.NETWORK_ERROR,
  },
];

export function classifyAuthError(error: unknown): AuthError {
  const errorName = getErrorName(error);
  const errorMessage = getErrorMessage(error);
  const httpStatus = isHttpError(error) ? error.status : undefined;

  for (const rule of ERROR_RULES) {
    if (rule.match(errorName, errorMessage, httpStatus)) {
      return {
        type: rule.type,
        message: rule.userMessage,
        originalError: error,
      };
    }
  }

  return {
    type: 'UNKNOWN_ERROR',
    message: AUTH_MESSAGES.GENERIC_ERROR,
    originalError: error,
  };
}

export function requiresReauthentication(error: AuthError): boolean {
  return (
    error.type === 'REFRESH_TOKEN_EXPIRED' ||
    error.type === 'UNAUTHENTICATED' ||
    error.type === 'TOKEN_EXPIRED'
  );
}

export function isRetryableError(error: AuthError): boolean {
  return error.type === 'NETWORK_ERROR';
}
